import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import api from '../utils/axios.ts';

const ApprovalHistory = () => {
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    approved_by: '',
    disbursed_by: '',
    date_from: '',
    date_to: '',
    page: 1,
    per_page: 15
  });

  const { data: approvalHistory, isLoading, error } = useQuery({
    queryKey: ['approval-history', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await api.get(`/memberloan/approval-history?${params.toString()}`);
      return response.data;
    },
    staleTime: 30000, // 30 seconds
  });

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  // const handleExport = () => {
  //   const params = new URLSearchParams();
  //   Object.entries(filters).forEach(([key, value]) => {
  //     if (value && key !== 'page' && key !== 'per_page') params.append(key, value);
  //   });
    
  //   // Create download link
  //   const url = `${api.defaults.baseURL}/memberloan/approval-history?${params.toString()}&export=true`;
  //   const link = document.createElement('a');
  //   link.href = url;
  //   link.download = `approval-history-${format(new Date(), 'yyyy-MM-dd')}.csv`;
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  // };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'APPROVED': { color: 'bg-green-100 text-green-800', text: 'Approved' },
      'DISAPPROVED': { color: 'bg-red-100 text-red-800', text: 'Disapproved' },
      'ACTIVE': { color: 'bg-blue-100 text-blue-800', text: 'Active' }
    };

    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', text: status };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading approval history
            </h3>
            <div className="mt-2 text-sm text-red-700">
              {error.message}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Approval History</h1>
            <p className="text-gray-600">
              View all loan approval and disbursement actions
            </p>
          </div>
          {/* <button
            onClick={handleExport}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Export CSV
          </button> */}
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Search loans..."
              className="w-full px-3 py-2 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="APPROVED">Approved</option>
              <option value="DISAPPROVED">Disapproved</option>
              <option value="ACTIVE">Active</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              From Date
            </label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              className="w-full px-3 py-2 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              To Date
            </label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              className="w-full px-3 py-2 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Per Page
            </label>
            <select
              value={filters.per_page}
              onChange={(e) => handleFilterChange('per_page', e.target.value)}
              className="w-full px-3 py-2 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="15">15</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({
                search: '',
                status: '',
                approved_by: '',
                disbursed_by: '',
                date_from: '',
                date_to: '',
                page: 1,
                per_page: 15
              })}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 kpi-grid">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {approvalHistory?.data?.length || 0}
            </div>
            <div className="text-sm text-blue-600">Total Actions</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {approvalHistory?.data?.filter(loan => loan.status === 'APPROVED').length || 0}
            </div>
            <div className="text-sm text-green-600">Approved</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {approvalHistory?.data?.filter(loan => loan.status === 'DISAPPROVED').length || 0}
            </div>
            <div className="text-sm text-red-600">Disapproved</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">
              {approvalHistory?.data?.filter(loan => loan.status === 'ACTIVE').length || 0}
            </div>
            <div className="text-sm text-purple-600">Active</div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white shadow rounded-lg">
        {/* Loading State */}
        {isLoading && (
          <div className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && (!approvalHistory?.data || approvalHistory.data.length === 0) && (
          <div className="p-12 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No approval history found</h3>
            <p className="text-gray-500">Try adjusting your filters to see more results.</p>
          </div>
        )}

        {/* Results Table */}
        {!isLoading && approvalHistory?.data && approvalHistory.data.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <table className="responsive-table min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Loan Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {approvalHistory.data.map((loan) => (
                    <tr key={loan.id} className="hover:bg-gray-50">
                      <td data-label="Loan Details" className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            #{loan.loan_no}
                          </div>
                          <div className="text-sm text-gray-500">
                            {loan.member?.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatCurrency(loan.principal)}
                          </div>
                        </div>
                      </td>
                      <td data-label="Status" className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(loan.status)}
                      </td>
                      <td data-label="Action By" className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {loan.approved_by?.name || loan.disbursed_by?.name || 'System'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {loan.approved_by?.email || loan.disbursed_by?.email}
                        </div>
                      </td>
                      <td data-label="Date" className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>{format(new Date(loan.updated_at), 'MMM dd, yyyy')}</div>
                        <div>{format(new Date(loan.updated_at), 'HH:mm')}</div>
                      </td>
                      <td data-label="Notes" className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {loan.approval_notes || loan.disbursement_notes || '-'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {approvalHistory.meta && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(filters.page - 1)}
                    disabled={filters.page <= 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(filters.page + 1)}
                    disabled={filters.page >= approvalHistory.meta.last_page}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">{(filters.page - 1) * filters.per_page + 1}</span>
                      {' '}to{' '}
                      <span className="font-medium">
                        {Math.min(filters.page * filters.per_page, approvalHistory.meta.total)}
                      </span>
                      {' '}of{' '}
                      <span className="font-medium">{approvalHistory.meta.total}</span>
                      {' '}results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(filters.page - 1)}
                        disabled={filters.page <= 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => handlePageChange(filters.page + 1)}
                        disabled={filters.page >= approvalHistory.meta.last_page}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ApprovalHistory; 